import { produce } from "immer";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { CmagDataWithDate } from "../../views/cmag";

const EntryCell = styled.td`
	text-align: right;
`;

const PivotCell = styled.td``;

const Expandible = styled.td`
	cursor: pointer;
`;

const Table = styled.table`
	border-collapse: collapse;
	empty-cells: hide;
	margin: auto;
	* {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	td {
		border-left: solid 1px #ddd;
		border-right: solid 1px #ddd;
		max-width: 10rem;
		min-width: 10rem;
		padding: 0 1rem;
	}
	th {
		border: solid 1px #ddd;
		max-width: 10rem;
		min-width: 10rem;
		text-transform: capitalize;
	}
`;

function nameToBgColor(name: string) {
	const lowerName = name.toLocaleLowerCase();
	if (lowerName === "democrat") return "#add8e6";
	if (lowerName === "republican") return "#ffcccb";
	if (lowerName === "independent") return "#90ee90";
	return "white";
}

type TableStyleProps = {
	name: string;
	striped?: boolean;
};
const TableBody = styled.tbody<TableStyleProps>`
	border: solid 1px #ddd;
	tr:first-child {
		background-color: ${props => nameToBgColor(props.name)};
	}
	tr:nth-child(even) {
		background-color: #eeeeee;
		td:first-child {
			background-color: #eeeeee;
		}
		td:nth-child(2) {
			background-color: #eeeeee;
		}
		td:nth-child(3) {
			background-color: #eeeeee;
	}
	tr:nth-child(odd) {
		td:first-child {
			background-color: #ffffff;
		}
		td:nth-child(2) {
			background-color: #ffffff;
		}
		td:nth-child(3) {
			background-color: #ffffff;
		}
	}
`;

const TableFooter = styled.tfoot`
	margin-top: 16rem;
`;

const TotalRow = styled.tr<TableStyleProps>`
	border: solid 1px #ddd;
	td:nth-child(n + 1):nth-child(-n + 3) {
		border: none;
		position: sticky;
	}
	td:first-child {
		left: 0;
		background-color: white;
	}
	td:nth-child(2) {
		left: 10rem;
		background-color: white;
	}
	td:nth-child(3) {
		left: 20rem;
		background-color: white;
	}
`;

const FooterSpace = styled.tr`
	height: 2rem;
	* {
		border: none;
	}
`;

const Row = styled.tr<TableStyleProps>`
	background-color: ${props => (props.striped ? "#eeeeee" : "#ffffff")};

	td:nth-child(n + 1):nth-child(-n + 3) {
		border: none;
		position: sticky;
	}
	td:first-child {
		background-color: #ffffff !important;
		left: 0;
	}
	td:nth-child(2) {
		background-color: #ffffff !important;
		left: 10rem;
	}
	td:nth-child(3) {
		background-color: #ffffff !important;
		left: 20rem;
	}
`;

const HeaderRow = styled.tr<TableStyleProps>`
	td:nth-child(n + 1):nth-child(-n + 3) {
		border: none;
		position: sticky;
	}
	td:first-child {
		background-color: ${props =>
		nameToBgColor(props.name)} !important;
		left: 0;
	}
	td:nth-child(2) {
		background-color: ${props =>
		nameToBgColor(props.name)} !important;
		left: 10rem;
	}
	td:nth-child(3) {
		background-color: ${props =>
		nameToBgColor(props.name)} !important;
		left: 20rem;
	}
`;

const TD = styled.td``;
const TH = styled.th``;
const TR = styled.tr``;
const THead = styled.thead<TableStyleProps>`
	border-top: solid 1px #ddd;
	th:nth-child(n + 1):nth-child(-n + 3) {
		border: none;
		position: sticky;
	}
	th:first-child {
		background-color: ${props => nameToBgColor(props.name)};
		left: 0;
	}
	th:nth-child(2) {
		background-color: ${props => nameToBgColor(props.name)};
		left: 10rem;
	}
	th:nth-child(3) {
		background-color: ${props => nameToBgColor(props.name)};
		left: 20rem;
	}
`;

type Pivot = string;

type PivotState = {
	children?: PivotState[];
	expanded: boolean;
	level: number;
	name: string;
	path: string;
	totals: any[];
	link?: string;
};

export type ColumnPivot = {
	dataIsInRange: (d: any, rangeIndex: number) => boolean;
	displayValue: (d: any) => string;
	property: string;
	rangeStarts: any[];
};

type TableProps = {
	columnPivot: ColumnPivot;
	data: CmagDataWithDate[];
	rowPivots: Pivot[];
};

type TableState = {
	children: PivotState[];
	totals: any[];
};

function totalRuns(acc: any, d: any): any {
	let { runs } = acc;

	runs += d.runs;
	return {
		runs
	};
}

function buildState(
	data: any[],
	level: number,
	path: string,
	columnPivot: ColumnPivot,
	rowPivots: Pivot[]
): PivotState[] {
	const isFinalLevel = level === rowPivots.length - 1;

	const uniquePivots = Array.from(
		new Set(data.map(d => d[rowPivots[level]]))
	);

	return uniquePivots.map(pivot => {
		const state: PivotState = {
			expanded: false,
			level,
			name: pivot,
			totals: [],
			path: `${path}/${pivot}`,
			children: []
		};

		const applicableData = data.filter(
			d => d[rowPivots[level]] === pivot
		);

		state.totals = columnPivot.rangeStarts.map(
			(rangeStart, rangeIndex) => {
				const columnData = applicableData.filter(d =>
					columnPivot.dataIsInRange(d, rangeIndex)
				);
				return columnData.reduce(totalRuns, {
					runs: 0
				});
			}
		);

		state.totals.push(
			state.totals.reduce(totalRuns, {
				runs: 0
			})
		);

		const nextLevel = level + 1;

		if (isFinalLevel) {
			const matching = applicableData.find(
				d => d[rowPivots[level]] === pivot
			);
			if (matching?.link) {
				state.link = matching.link;
			}
		} else {
			state.children = buildState(
				applicableData,
				nextLevel,
				state.path,
				columnPivot,
				rowPivots
			);
		}

		return state;
	});
}

function renderRow(
	totalLevels: number,
	state: PivotState,
	handleToggleExpand: (state: PivotState) => void,
	partyName: string,
	headerRow: boolean = false,
	striped: boolean = false
): JSX.Element {
	const rowCells = [];
	const RowTag = headerRow && !striped ? HeaderRow : Row;

	for (let level = 0; level < totalLevels; level++) {
		const key = `${state.name}-${level}`;

		if (level === totalLevels - 1) {
			if (state.link) {
				rowCells.push(
					<PivotCell key={`${state.path}-url`}>
						<a
							href={state.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							{state.name}
						</a>
					</PivotCell>
				);
			} else {
				rowCells.push(
					<PivotCell
						key={`${state.path}-url-empty`}
					/>
				);
			}
			continue;
		}

		if (state.level === level) {
			if (state.children) {
				const marker = state.expanded ? "-" : "+";
				rowCells.push(
					<Expandible
						key={key}
						onClick={() =>
							handleToggleExpand(
								state
							)
						}
					>
						{`${marker} ${state.name}`}
					</Expandible>
				);
			} else {
				rowCells.push(
					<PivotCell key={key}>
						{state.name}
					</PivotCell>
				);
			}
		} else {
			rowCells.push(<PivotCell key={key} />);
		}
	}

	rowCells.push(renderTotals(state.name, state.totals));

	return (
		<RowTag
			key={`${state.path}-${state.name}`}
			name={partyName}
			striped={striped}
		>
			{rowCells}
		</RowTag>
	);
}

function renderRowsForPivot(
	totalLevels: number,
	state: PivotState,
	handleToggleExpand: (state: PivotState) => void,
	partyName: string,
	headerRow: boolean = false,
	colSpan: number,
	startingIndex: number = 0
): JSX.Element[] {
	const rows: JSX.Element[] = [];
	let rowCount = 0;

	rows.push(
		renderRow(
			totalLevels,
			state,
			handleToggleExpand,
			partyName,
			headerRow,
			(startingIndex + rowCount) % 2 === 1
		)
	);
	rowCount++;

	if (state.expanded && state.children) {
		for (const child of state.children
			.filter(c => typeof c.name === "string")
			.sort((a, b) => a.name.localeCompare(b.name))) {
			const childRows = renderRowsForPivot(
				totalLevels,
				child,
				handleToggleExpand,
				partyName,
				true,
				colSpan,
				startingIndex + rowCount
			);
			rows.push(...childRows);
			rowCount += childRows.length;
		}
	}

	return rows;
}

function renderTotals(pivot: string, totals: any[]): JSX.Element[] {
	return totals.map((total, index) => (
		<>
			<EntryCell key={`${pivot}-${index}-runs`}>
				{total.runs}
			</EntryCell>
		</>
	));
}

const PivotTableCmag = (props: TableProps) => {
	const [tableState, setTableState] = useState<TableState>();

	function toggleExpanded(state: PivotState) {
		setTableState(prevState => {
			return produce(prevState, draft => {
				const pathParts = state.path
					.split("/")
					.filter(p => !!p);
				let current:
					| PivotState
					| TableState
					| undefined = draft;
				for (
					let i = 0;
					i < pathParts.length && pathParts[i];
					i++
				) {
					const children:
						| PivotState[]
						| undefined = current?.children;
					current = children?.find(
						c => c.name === pathParts[i]
					);
				}
				if (current?.hasOwnProperty("expanded")) {
					const target = current as PivotState;
					target.expanded = !target.expanded;
				}
			});
		});
	}

	useEffect(() => {
		const children: PivotState[] = buildState(
			props.data,
			0,
			"",
			props.columnPivot,
			props.rowPivots
		);
		const totals = children.reduce((acc, child) => {
			if (!acc.length) {
				return child.totals.map(v => ({ ...v }));
			} else {
				return acc.map((entry, idx) => {
					Object.keys(entry).forEach(key => {
						entry[key] =
							entry[key] +
							child.totals[idx][key];
					});
					return entry;
				});
			}
		}, [] as any[]);
		setTableState({
			children,
			totals
		});
	}, [props.data]);

	const colSpan = props.columnPivot.rangeStarts.length + 1;

	return (
		<Table key="table">
			<THead name="" key="thead">
				<TR key="total-header">
					<TH key="0-header"></TH>
					<TH key="1-header"></TH>
					<TH key="2-header"></TH>
					{props.columnPivot.rangeStarts.map(
						rangeStart => (
							<TH
								key={`${rangeStart}-header`}
							>
								{props.columnPivot.displayValue(
									rangeStart
								)}
							</TH>
						)
					)}
					<TH key="total-header">Total</TH>
				</TR>
				<TR key="header">
					{props.rowPivots.map(pivot => (
						<>
							<TH
								key={`${pivot}-header`}
							>
								{pivot}
							</TH>
						</>
					))}
					{props.columnPivot.rangeStarts.map(
						rangeStart => (
							<>
								<TH
									key={`${rangeStart}-runs-header`}
								>
									Runs
								</TH>
							</>
						)
					)}
					<TH key="runs-header">Runs</TH>
				</TR>
			</THead>

			{!!tableState &&
				tableState.children.flatMap(child => {
					return (
						<TableBody
							key={`tbody-${child.name}`}
							name={child.name}
						>
							{renderRowsForPivot(
								props.rowPivots
									.length,
								child,
								toggleExpanded,
								child.name,
								true,
								colSpan
							)}
						</TableBody>
					);
				})}
			<TableFooter key="tfoot">
				<FooterSpace key="footer-spacing"></FooterSpace>
				<TotalRow name="" key="grand-totals">
					<TD key="total-grand-totals">Total</TD>
					<TD key="1-grand-totals"></TD>
					<TD key="2-grand-totals"></TD>
					{!!tableState &&
						renderTotals(
							"total",
							tableState.totals
						)}
				</TotalRow>
			</TableFooter>
		</Table>
	);
};

export default PivotTableCmag;
